import client from '@/graphql/client'
import CreateWorkflow from '@/graphql/mutations/CreateWorkflow'
import DeleteWorkflow from '@/graphql/mutations/DeleteWorkflow'
import CloneWorkflow from '@/graphql/mutations/CloneWorkflow'
import ListWorkflows from '@/graphql/queries/ListWorkflows'
import ListDemoWorkflows from '@/graphql/queries/ListDemoWorkflows'
import OnCreateOrUpdateWorkflow from '@/graphql/subscriptions/OnCreateOrUpdateWorkflow'
import OnDeleteWorkflow from '@/graphql/subscriptions/OnDeleteWorkflow'
import { addToSubscriptions } from '@/api/subscriptionHelpers'

export const workflows = {
  namespaced: true,
  state: {
    workflows: null,
    demoWorkflows: null,
    subscriptions: null
  },
  mutations: {
    setWorkflows (state, payload) {
      state.workflows = payload
    },
    setSubscriptions (state, payload) {
      state.subscriptions = payload
    },
    unsubscribeFromAll (state) {
      if (state.subscriptions) {
        for (const subscription of state.subscriptions) {
          subscription.unsubscribe()
        }
      }
      state.subscriptions = null
    },
    updateWorkflows (state, payload) {
      const index = state.workflows?.findIndex((workflow) => workflow.id === payload.id)
      if (index >= 0) {
        state.workflows[index] = Object.assign(state.workflows[index], payload)
      } else {
        if (!state.workflows) { state.workflows = [] }
        state.workflows.unshift(payload)
      }
    },
    deleteWorkflow (state, payload) {
      const index = state.workflows?.findIndex((workflow) => workflow.id === payload.id)
      if (index >= 0) {
        state.workflows.splice(index, 1)
      }
    },
    setDemoWorkflows (state, payload) {
      state.demoWorkflows = payload
    }
  },
  actions: {
    async createWorkflow ({ commit }, { workflow, silent = false }) {
      try {
        const { createWorkflow } = await client.query({
          operationName: 'createWorkflow',
          query: CreateWorkflow,
          variables: {
            projectId: workflow.projectId,
            input: {
              name: workflow.name
            }
          }
        })
        commit('updateWorkflows', createWorkflow)
        if (!silent) {
          commit('snackbar/showMessage', { content: 'Created' }, { root: true })
        }
        return createWorkflow.id
      } catch (e) {
        console.log(e)
        commit('snackbar/showMessage', {
          content: e?.message || 'Error creating workflow.',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }
    },

    async deleteWorkflow ({ commit }, { projectId, workflowId }) {
      try {
        const { deleteWorkflow } = await client.query({
          operationName: 'deleteWorkflow',
          query: DeleteWorkflow,
          variables: {
            projectId,
            workflowId
          }
        })
        commit('deleteWorkflow', deleteWorkflow.deleteWorkflow)
        commit('snackbar/showMessage', { content: 'Updated' }, { root: true })
        return deleteWorkflow.deleteWorkflow.id
      } catch (e) {
        console.log(e)
      }
    },

    async cloneWorkflow ({ commit }, { targetProjectId, workflowId, silent = false }) {
      if (!silent) {
        commit('snackbar/showMessage', { content: 'Copying workflow...', showProgress: true, timeout: -1 }, { root: true })
      }
      try {
        commit('loading/startLoading', null, { root: true })
        const { cloneWorkflow } = await client.query({
          operationName: 'cloneWorkflow',
          query: CloneWorkflow,
          variables: {
            workflowId,
            projectId: targetProjectId
          }
        })
        commit('loading/stopLoading', null, { root: true })
        if (!silent) {
          commit('snackbar/showMessage', { content: 'Workflow has been successfully copied.' }, { root: true })
        }
        return cloneWorkflow
      } catch (e) {
        commit('loading/stopLoading', null, { root: true })
        commit('snackbar/showMessage', { content: 'Error copying workflow.', color: 'red' }, { root: true })
        console.log(e)
      }
    },

    async listWorkflows ({ commit, rootState }, projectId) {
      let allWorkflows = []
      let nextToken = null

      do {
        const res = await client.query({
          operationName: 'listWorkflows',
          query: ListWorkflows,
          variables: { projectId, limit: 100, nextToken }
        }, {
          failSilently: true
        })

        if (!res || !res.listWorkflows) {
          commit('setWorkflows', null)
          return
        }

        const workflows = res.listWorkflows?.project?.workflows
        allWorkflows = allWorkflows.concat(workflows?.items || [])
        nextToken = workflows?.nextToken
      } while (nextToken)

      commit('setWorkflows', allWorkflows)

      if (rootState.isUserLoggedIn === false || rootState.workflow.isViewOnlyEnabled === true) {
        return
      }

      // Unsubscribe from previous subscriptions before creating new ones
      commit('unsubscribeFromAll')

      const subscriptions = []
      const addSubscription = (query, variables, callback) => {
        const sub = addToSubscriptions(query, variables, callback)
        subscriptions.push(sub)
      }

      addSubscription(OnCreateOrUpdateWorkflow, { projectId }, (data) => {
        if (data.onCreateOrUpdateWorkflow?.projectId !== projectId) {
          // To prevent unnecessary updates if the created/updated workflow is not in the current project
          return
        }
        const updatedWorkflow = data.onCreateOrUpdateWorkflow
        commit('updateWorkflows', updatedWorkflow)

        // Notify workflow module (it will ignore if not the current workflow)
        commit('workflow/updateWorkflow', updatedWorkflow, { root: true })
        commit('setWsMessageForCypress', { version: updatedWorkflow.version }, { root: true })

        // Notify workflowHistory module (it will ignore if not tracking this workflow)
        try {
          commit('workflowHistory/addHistoryRecord', updatedWorkflow, { root: true })
        } catch (e) {
          // If there's a gap in history, workflowHistory will handle the error internally
          console.log('History record update failed:', e.message)
        }
      })

      addSubscription(OnDeleteWorkflow, { projectId }, (data) => {
        const deletedWorkflow = data.onDeleteWorkflow.deleteWorkflow
        commit('deleteWorkflow', deletedWorkflow)
        // Notify the workflow module in case it has this workflow open
        commit('workflow/deleteWorkflow', deletedWorkflow, { root: true })
      })

      commit('setSubscriptions', subscriptions)
    },

    async listDemoWorkflows ({ commit }) {
      const res = await client.query({
        operationName: 'listDemoWorkflows',
        query: ListDemoWorkflows
      })
      commit('setDemoWorkflows', res.listDemoWorkflows)
    }
  }
}
